'use memo'

// web/src/features/event/components/EventTimezoneField.tsx
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  buildTimezoneOptions,
  resolveZoneOptionValue,
} from '@sportninja/common/features/event/timezoneOptions'
import type { EventFormValues } from '@sportninja/common/features/event/types'
import { SelectField } from 'src/features/profile/player/setupModal/components/FormikField'
import { rebaseEventZone } from '../utils/rebaseEventZone'

/** Every date field on the form is authored in the selected zone, so all three
 *  rebase together when it changes. */
const ZONED_FIELDS = ['starts_at', 'ends_at', 'repeatUntil'] as const

/**
 * The event's timezone — the zone every date/time on this form is authored in.
 *
 * Pre-selected from the admin's account timezone (browser zone as fallback);
 * see `EventModal`, which seeds `values.timezone`.
 *
 * Changing it holds the WALL-CLOCK and moves the instant: an event typed as
 * 6 PM under Toronto, switched to Vancouver, stays 6 PM and is sent three hours
 * later. The pickers keep showing what the admin typed, and the payload carries
 * the correspondingly-shifted UTC instants. See `rebaseInstantToZone`.
 */
const EventTimezoneField: React.FC = () => {
  const { values, setValues } = useFormikContext<EventFormValues>()
  // Resolved through the IANA alias table: an account stored as 'Asia/Kolkata'
  // has to match the list's 'Asia/Calcutta' entry, or the field renders its
  // placeholder instead of the zone the event is actually authored in.
  const timezone = resolveZoneOptionValue(values.timezone)

  // `timezone` is passed as the extra zone so a stored value the runtime does
  // not list still appears as the selected option instead of a blank select.
  // The list is ~420 entries and this form re-renders on every keystroke, so
  // the 'use memo' directive above is doing real work here.
  const options = buildTimezoneOptions(timezone)

  const handleChange = (next: string) => {
    if (!next || next === timezone) {
      return
    }

    // ONE update, not four. Each `setFieldValue` is a separate render, and this
    // form re-renders the whole field tree — so writing the three dates and the
    // zone separately made the selection visibly lag before it applied.
    setValues((current) => {
      // Rebase against the PREVIOUS zone, so each date is read in the zone it
      // was authored in. Skipped when the form has no prior zone — there is
      // nothing to reinterpret from.
      if (!timezone) {
        return { ...current, timezone: next }
      }

      const rebased = ZONED_FIELDS.reduce<Partial<EventFormValues>>(
        (acc, field) => {
          const value = current[field]
          if (value) {
            acc[field] = rebaseEventZone(value, timezone, next)
          }
          return acc
        },
        {}
      )

      return { ...current, ...rebased, timezone: next }
    })
  }

  return (
    <SelectField
      name='timezone'
      label={capitalizeWords('Timezone')}
      placeholder={capitalizeWords('Select timezone')}
      options={options}
      onChange={handleChange}
      showSearch
      required
    />
  )
}

export default EventTimezoneField
